import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader, ArrowDownWideNarrow, ArrowUpWideNarrow, GripHorizontal, FilterX, Link2, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react';
import { devFilesApi, dynamicApi, appCfgApi } from '../../services/api';
import FilterableDataGrid from './FilterableDataGrid';
import ModuleHeader from './ModuleHeader';
import ListPanel from './ListPanel';
import FKRecordModal from './FKRecordModal';
import SearchSelect from './SearchSelect';
import { tableNameToSlug } from './DynamicModulePage';
import { useAuth } from '../../context/AuthContext';
import { useLookups } from '../../context/LookupContext';
import { useNotifications } from '../../context/NotificationContext';

/**
 * DynamicModule - Config-driven module component
 * Loads layout from module-configs/{tableName}.json
 * Uses dynamic API for automatic FK display value resolution
 * FK clicks open a modal popup instead of navigating away
 *
 * URL structure:
 * - /buildings           → First record, panel collapsed
 * - /buildings/123       → Record 123, panel collapsed
 * - /buildings/123?panel=open  → Record 123, panel expanded
 * - /buildings?search=FAB      → Search for FAB
 *
 * Props:
 * - tableName: Database table name (e.g., "Source", "Building")
 * - urlRecordId: Record ID from URL (optional)
 * - urlSearch: Search term from URL (optional)
 * - urlPanelOpen: Panel open state from URL (optional)
 * - urlRelatedCollapsed: Related panel collapsed state from URL (optional)
 */
// Tables that use split-load: fast List View (TOP 100) + background Datasheet + server-side search
const LARGE_TABLES = new Set(['Tags', 'Panels']);

const DynamicModule = ({ tableName, urlRecordId, urlSearch, urlPanelOpen, urlRelatedCollapsed, fieldOverrides, forceViewType, externalModuleConfig, headerTabs, allowBulkDelete = true, derivedDatasheetColumns = [] }) => {
  const location = useLocation();
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const { canEdit: userCanEdit, currentUser } = useAuth();
  const { getValues: getLookupValues } = useLookups();
  const { notify } = useNotifications();
  const appliedNavState = useRef(false);
  const appliedUrlState = useRef(false);
  const [viewMode, setViewMode] = useState('list');
  const [allData, setAllData] = useState([]);
  const [dataTableName, setDataTableName] = useState(null); // Track which table the data belongs to
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(urlSearch || '');
  const [activeSearch, setActiveSearch] = useState(urlSearch || ''); // only updates on Enter submit
  const [filters, setFilters] = useState({});
  const [listPanelCollapsed, setListPanelCollapsed] = useState(false);
  const [scrollToSelected, setScrollToSelected] = useState(false); // Only true on deep link navigation

  // Split-load state — large tables only (Tags, Panels)
  const [listData, setListData] = useState([]);           // fast path: TOP 100 for List View
  const [datasheetData, setDatasheetData] = useState([]); // background full load for Datasheet
  const [datasheetLoading, setDatasheetLoading] = useState(false);
  const [listSearchResults, setListSearchResults] = useState([]);
  const [isListSearchActive, setIsListSearchActive] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Config-driven layout
  const [moduleConfig, setModuleConfig] = useState(null);
  const [fieldMetadata, setFieldMetadata] = useState({});
  const [appSettings, setAppSettings] = useState({});

  // Derived field cache — key: "TableName:recordId" → fetched record object
  const [derivedFieldCache, setDerivedFieldCache] = useState({});


  // CRUD state
  const [isEditing, setIsEditing] = useState(false);
  const [isNewRecord, setIsNewRecord] = useState(false); // true when adding a new record (POST vs PUT)
  const [editedRecord, setEditedRecord] = useState(null); // Copy of selectedItem during edit
  const [dirtyFields, setDirtyFields] = useState(new Set()); // Track which fields have changed
  const [fkOptions, setFkOptions] = useState({}); // FK dropdown options keyed by fkTable name

  // Datasheet edit state
  const [editModeEnabled, setEditModeEnabled] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isDatasheetSaving, setIsDatasheetSaving] = useState(false);
  const [originalData, setOriginalData] = useState([]);

  // Datasheet pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Delete mode state
  const [deleteModeEnabled, setDeleteModeEnabled] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());

  // Column settings state (session-based)
  const [columnOrder, setColumnOrder] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [frozenColumns, setFrozenColumns] = useState(null); // null = uninitialized, auto-defaults to first column


  // Related tables state
  const [activeRelatedTab, setActiveRelatedTab] = useState(0);
  const [relatedData, setRelatedData] = useState({});
  const [relatedLoading, setRelatedLoading] = useState({}); // Track loading per table
  const [relatedFilters, setRelatedFilters] = useState({});
  const [relatedMetadata, setRelatedMetadata] = useState({}); // Field metadata keyed by tableName

  // Related panel resizable state
  const [relatedPanelHeight, setRelatedPanelHeight] = useState(33); // percentage, default 33%
  const [relatedPanelCollapsed, setRelatedPanelCollapsed] = useState(urlRelatedCollapsed || false);
  const [isResizing, setIsResizing] = useState(false);
  const detailPanelRef = useRef(null);

  // FK Modal state
  const [fkModalOpen, setFkModalOpen] = useState(false);
  const [fkModalTable, setFkModalTable] = useState(null);
  const [fkModalRecordId, setFkModalRecordId] = useState(null);

  // Build URL from current state
  const buildUrl = useCallback((recordId, search, panelOpen, relatedCollapsed) => {
    const slug = tableNameToSlug[tableName] || tableName.toLowerCase();
    let url = `/${slug}`;
    if (recordId) {
      url += `/${recordId}`;
    }
    const params = new URLSearchParams();
    if (search) {
      params.set('search', search);
    }
    if (panelOpen) {
      params.set('panel', 'open');
    }
    if (relatedCollapsed) {
      params.set('related', 'collapsed');
    }
    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  }, [tableName]);

  // Update URL without navigation (replaceState for selection changes)
  const updateUrl = useCallback((recordId, search, panelOpen, relatedCollapsed, push = false) => {
    const url = buildUrl(recordId, search, panelOpen, relatedCollapsed);
    if (push) {
      window.history.pushState({}, '', url);
    } else {
      window.history.replaceState({}, '', url);
    }
  }, [buildUrl]);

  // Load config and data on mount or when tableName changes
  useEffect(() => {
    // Reset state when switching tables
    setLoading(true);
    setAllData([]);
    setDataTableName(null); // Clear data ownership
    setSelectedItem(null);
    setModuleConfig(null);
    setFieldMetadata({});
    setRelatedData({});
    setSearchTerm(urlSearch || '');
    setActiveSearch(urlSearch || '');
    setFilters({});
    setViewMode('list');
    setListPanelCollapsed(false);
    setRelatedPanelCollapsed(urlRelatedCollapsed || false);
    setRelatedPanelHeight(33); // Reset to default
    // Reset split-load state
    setListData([]);
    setDatasheetData([]);
    setDatasheetLoading(false);
    setListSearchResults([]);
    setIsListSearchActive(false);
    setSearchLoading(false);
    appliedNavState.current = false;
    appliedUrlState.current = false;

    const loadData = async () => {
      try {
        // Load config — use external (data entry modules) if provided, else fetch from API
        if (externalModuleConfig) {
          setModuleConfig(externalModuleConfig);
        } else {
          try {
            const configRes = await devFilesApi.getModuleConfig(tableName);
            setModuleConfig(configRes.data);
          } catch (err) {
            console.warn(`No config found for ${tableName}, using defaults`);
            setModuleConfig(null);
          }
        }

        // Load field metadata for FK display fields
        try {
          const metadataRes = await appCfgApi.getFieldMetadata(tableName);
          const metadataMap = {};
          (metadataRes.data || []).forEach(m => {
            metadataMap[m.field_Name] = m;
          });
          setFieldMetadata(metadataMap);
        } catch (err) {
          // Metadata might not exist - that's OK
        }

        // Load app settings for date formatting
        try {
          const settingsRes = await appCfgApi.getDisplaySettings();
          const settingsMap = {};
          (settingsRes.data || []).forEach(s => {
            settingsMap[s.setting_Key] = s.setting_Value;
          });
          setAppSettings(settingsMap);
        } catch (err) {
          // Settings might not exist - use defaults
        }

        // Load data via dynamic API (auto-resolves FK display values)
        if (LARGE_TABLES.has(tableName) && !urlRecordId) {
          // Fast path — show list immediately with TOP 100
          const listRes = await dynamicApi.getAll(tableName, { limit: 100 });
          setListData(listRes.data);
          setDataTableName(tableName);
          setDatasheetLoading(true);
          // Background load full dataset for Datasheet
          dynamicApi.getAll(tableName).then(res => {
            setDatasheetData(res.data);
            setOriginalData(res.data);
            setDatasheetLoading(false);
          }).catch(err => {
            console.error(`Background load failed for ${tableName}:`, err);
            setDatasheetLoading(false);
          });
        } else if (LARGE_TABLES.has(tableName) && urlRecordId) {
          // Deep link — load all records so the target record is guaranteed present
          const res = await dynamicApi.getAll(tableName);
          setListData(res.data);
          setDatasheetData(res.data);
          setOriginalData(res.data);
          setDatasheetLoading(false);
          setDataTableName(tableName);
        } else {
          // Non-large — existing single-fetch path
          const dataRes = await dynamicApi.getAll(tableName);
          setAllData(dataRes.data);
          setDataTableName(tableName);
          setOriginalData(dataRes.data);
        }
      } catch (err) {
        console.error(`Failed to load ${tableName} module:`, err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, urlSearch, urlPanelOpen]);

  // Handle incoming navigation context from React Router state
  // This is used when navigating via "Go to full page" from the FK modal
  useEffect(() => {
    const navState = location.state;
    const activeData = LARGE_TABLES.has(tableName) ? listData : allData;

    // Wait until data for the CURRENT table has loaded
    if (!navState || activeData.length === 0 || appliedNavState.current || dataTableName !== tableName) {
      return;
    }

    appliedNavState.current = true;

    // Direct navigation with selectedId (from "Go to full page" in modal)
    if (navState.selectedId) {
      const record = activeData.find(item => item.id === navState.selectedId);
      if (record) {
        setSelectedItem(record);
        setListPanelCollapsed(true);
        setScrollToSelected(true);
        setTimeout(() => setScrollToSelected(false), 500);
      }
    }

    window.history.replaceState({}, document.title);
  }, [location.state, allData, listData, tableName, dataTableName]);

  // Apply URL state when data loads (record ID from URL)
  useEffect(() => {
    const activeData = LARGE_TABLES.has(tableName) ? listData : allData;
    if (activeData.length === 0 || appliedUrlState.current || dataTableName !== tableName) {
      return;
    }

    appliedUrlState.current = true;

    // If URL has a record ID, select that record and scroll to it
    if (urlRecordId) {
      const record = activeData.find(item => item.id === urlRecordId);
      if (record) {
        setSelectedItem(record);
        setScrollToSelected(true);
        // Reset scroll flag after scroll completes
        setTimeout(() => setScrollToSelected(false), 500);
        return;
      }
    }

    // Otherwise select first item (if not coming from location.state)
    if (!location.state) {
      setSelectedItem(activeData[0]);
    }
  }, [allData, listData, urlRecordId, location.state, dataTableName, tableName]);

  // Load related table data when selectedItem changes - parallel loading with per-table state
  useEffect(() => {
    if (!selectedItem || !moduleConfig?.relatedTables?.length) {
      setRelatedData({});
      setRelatedLoading({});
      return;
    }

    // Clear stale data and set all tables to loading
    setRelatedData({});
    const initialLoading = {};
    moduleConfig.relatedTables.forEach(t => {
      initialLoading[t.tableName] = true;
    });
    setRelatedLoading(initialLoading);

    // Load each table in parallel
    moduleConfig.relatedTables.forEach(async (relatedTable) => {
      try {
        // Load metadata for this related table
        let metadataMap = {};
        try {
          const metadataRes = await appCfgApi.getFieldMetadata(relatedTable.tableName);
          (metadataRes.data || []).forEach(m => {
            metadataMap[m.field_Name] = m;
          });
        } catch {
          // Metadata optional
        }
        setRelatedMetadata(prev => ({ ...prev, [relatedTable.tableName]: metadataMap }));

        // Use filtered dynamic API - filter at database level for performance
        const filterValue = selectedItem.id;
        const response = await dynamicApi.getFiltered(
          relatedTable.tableName,
          relatedTable.fkField,
          filterValue
        );
        let filteredRecords = response.data || [];

        // Sort if configured
        if (relatedTable.sortField) {
          const sortKey = relatedTable.sortField.charAt(0).toLowerCase() + relatedTable.sortField.slice(1);
          const direction = relatedTable.sortDirection === 'DESC' ? -1 : 1;
          filteredRecords.sort((a, b) => {
            const aVal = a[sortKey] ?? '';
            const bVal = b[sortKey] ?? '';
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
          });
        }

        // Update data and clear loading for this table
        setRelatedData(prev => ({ ...prev, [relatedTable.tableName]: filteredRecords }));
        setRelatedLoading(prev => ({ ...prev, [relatedTable.tableName]: false }));
      } catch (err) {
        console.error(`Failed to load related data for ${relatedTable.tableName}:`, err);
        setRelatedData(prev => ({ ...prev, [relatedTable.tableName]: [] }));
        setRelatedLoading(prev => ({ ...prev, [relatedTable.tableName]: false }));
      }
    });
  }, [selectedItem, moduleConfig?.relatedTables]);

  // Build columns from config
  // For FK fields, use the display field key (e.g., building_Code instead of building)
  const columns = useMemo(() => {
    // Sample row to check for _Display suffix pattern
    // For large tables use listData (available from fast path); fallback to allData
    const sampleRow = (LARGE_TABLES.has(tableName) ? listData[0] : allData[0]) || {};

    // Helper to get the correct key for a field
    const getFieldKey = (fieldName) => {
      const baseKey = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);

      // Check for _Display suffix pattern first (backend convention)
      const displaySuffixKey = baseKey + '_Display';
      if (sampleRow[displaySuffixKey] !== undefined) {
        return displaySuffixKey;
      }

      // Fall back to metadata FK display field
      const meta = fieldMetadata[fieldName];
      if (meta?.fK_Display_Field) {
        const metaKey = meta.fK_Display_Field.charAt(0).toLowerCase() + meta.fK_Display_Field.slice(1);
        if (sampleRow[metaKey] !== undefined) {
          return metaKey;
        }
      }

      // Case-insensitive fallback — handles all-caps fields like EU → eu
      if (sampleRow[baseKey] === undefined) {
        const ciKey = Object.keys(sampleRow).find(k => k.toLowerCase() === fieldName.toLowerCase());
        if (ciKey) return ciKey;
      }

      return baseKey;
    };

    // If externalModuleConfig has inline derived cols, build fully-ordered list (respects DEMC order)
    if (moduleConfig?.datasheetPanel?.columns?.some(c => c.isDerived)) {
      return moduleConfig.datasheetPanel.columns
        .filter(col => col.isDerived || fieldOverrides?.[col.fieldName]?.entryMode !== 'hidden')
        .map(col => {
          if (col.isDerived) {
            return {
              key: col.fieldName,
              label: col.label,
              width: col.width || 150,
              alignment: col.alignment || 'left',
              fieldName: col.fieldName,
              readOnly: true
            };
          }
          const meta = fieldMetadata[col.fieldName];
          const isCurrency = meta?.uI_Type?.toLowerCase() === 'currency';
          return {
            key: getFieldKey(col.fieldName),
            label: col.label,
            width: col.width || 150,
            alignment: col.alignment || 'left',
            fieldName: col.fieldName,
            headerVertical: meta?.header_Vertical || false,
            ...(isCurrency && { formatter: (v) => v != null && v !== '' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v)) : '' })
          };
        });
    }

    // Use datasheetPanel config if available (configured in List View Config editor)
    let regularCols;
    if (moduleConfig?.datasheetPanel?.columns?.length > 0) {
      regularCols = moduleConfig.datasheetPanel.columns
        .filter(col => col.visible !== false)
        .filter(col => fieldOverrides?.[col.fieldName]?.entryMode !== 'hidden')
        .map(col => {
          const meta = fieldMetadata[col.fieldName];
          const isCurrency = meta?.uI_Type?.toLowerCase() === 'currency';
          return {
            key: getFieldKey(col.fieldName),
            label: col.label,
            width: col.width || 150,
            alignment: col.alignment || 'left',
            fieldName: col.fieldName, // Keep original for metadata lookup
            headerVertical: meta?.header_Vertical || false,
            ...(isCurrency && { formatter: (v) => v != null && v !== '' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v)) : '' })
          };
        });
    } else if (!moduleConfig?.detailPanel?.fields) {
      regularCols = [];
    } else {
      // Fallback: derive columns from detailPanel fields
      regularCols = moduleConfig.detailPanel.fields
        .filter(f => f.fieldType !== 'section')
        .filter(f => fieldOverrides?.[f.fieldName]?.entryMode !== 'hidden')
        .map(f => {
          const meta = fieldMetadata[f.fieldName];
          const isCurrency = meta?.uI_Type?.toLowerCase() === 'currency';
          return {
            key: getFieldKey(f.fieldName),
            label: f.label,
            width: 150,
            alignment: 'left',
            fieldName: f.fieldName,
            headerVertical: meta?.header_Vertical || false,
            ...(isCurrency && { formatter: (v) => v != null && v !== '' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v)) : '' })
          };
        });
    }

    // Append derived datasheet columns (DEMC-configured, always read-only)
    const derivedCols = derivedDatasheetColumns.map(dc => ({
      key: dc.fieldName,
      label: dc.label || dc.targetField || dc.fieldName,
      width: dc.width || 150,
      alignment: dc.alignment || 'left',
      fieldName: dc.fieldName,
      readOnly: true
    }));

    return [...regularCols, ...derivedCols];
  }, [moduleConfig, fieldMetadata, allData, listData, tableName, fieldOverrides, derivedDatasheetColumns]);

  // Format data for datasheet display (dates)
  // Large tables: format datasheetData; non-large: format allData
  const formattedAllData = useMemo(() => {
    const source = LARGE_TABLES.has(tableName) ? datasheetData : allData;
    if (!source.length || !Object.keys(fieldMetadata).length) return source;

    return source.map(row => {
      const formatted = { ...row };
      // Format date fields
      Object.entries(fieldMetadata).forEach(([fieldName, meta]) => {
        if (meta?.uI_Type === 'date' || meta?.uI_Type === 'readonly') {
          const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
          if (formatted[key] && typeof formatted[key] === 'string' && formatted[key].includes('T')) {
            try {
              const date = new Date(formatted[key]);
              if (!isNaN(date.getTime())) {
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const year = date.getUTCFullYear();
                formatted[key] = `${month}/${day}/${year}`;
              }
            } catch { /* keep original */ }
          }
        }
      });
      return formatted;
    });
  }, [allData, datasheetData, fieldMetadata, tableName]);

  // Prefetch derived field data for all rows in the datasheet.
  // Fetches the entire FK table once per unique fkTable and caches by ID.
  // getAll is used (not getById) to avoid per-record HTTP calls and routing edge cases.
  useEffect(() => {
    const source = LARGE_TABLES.has(tableName) ? datasheetData : allData;
    if (!derivedDatasheetColumns.length) return;
    if (!source.length) return;

    // Collect unique FK tables needed
    const fkTables = [...new Set(
      derivedDatasheetColumns
        .map(dc => dc.fkTable || fieldMetadata[dc.sourceField]?.fK_Table)
        .filter(Boolean)
    )];

    fkTables.forEach(async resolvedFkTable => {
      // Check if any row in source references an ID not yet cached
      const sourceField = derivedDatasheetColumns.find(
        dc => (dc.fkTable || fieldMetadata[dc.sourceField]?.fK_Table) === resolvedFkTable
      )?.sourceField;
      if (!sourceField) return;

      const uniqueIds = [...new Set(
        source.map(row => {
          const sourceKey = Object.keys(row).find(k => k.toLowerCase() === sourceField.toLowerCase());
          return sourceKey ? row[sourceKey] : null;
        }).filter(v => v != null && v !== 0 && v !== '')
      )];

      const anyMissing = uniqueIds.some(id => !derivedFieldCache[`${resolvedFkTable}:${id}`]);
      if (!anyMissing) return;

      try {
        const res = await dynamicApi.getAll(resolvedFkTable);
        const records = res.data || [];
        const newEntries = {};
        records.forEach(rec => {
          if (rec.id != null) newEntries[`${resolvedFkTable}:${rec.id}`] = rec;
        });
        if (Object.keys(newEntries).length) {
          setDerivedFieldCache(prev => ({ ...prev, ...newEntries }));
        }
      } catch { /* silently skip if FK table unavailable */ }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, datasheetData, derivedDatasheetColumns]);

  // Augment formattedAllData with derived column values (_drv_* keys)
  const augmentedData = useMemo(() => {
    if (!derivedDatasheetColumns.length) return formattedAllData;
    return formattedAllData.map(row => {
      const augmented = { ...row };
      derivedDatasheetColumns.forEach(dc => {
        const { sourceField, fkTable, targetField, fieldName } = dc;
        const resolvedFkTable = fkTable || fieldMetadata[sourceField]?.fK_Table;
        if (!resolvedFkTable) return;
        const sourceKey = Object.keys(row).find(k => k.toLowerCase() === sourceField.toLowerCase());
        const fkId = sourceKey ? row[sourceKey] : null;
        if (!fkId) return;
        const cacheKey = `${resolvedFkTable}:${fkId}`;
        const related = derivedFieldCache[cacheKey];
        if (!related) return;
        const targetKey = Object.keys(related).find(k => k.toLowerCase() === targetField.toLowerCase());
        augmented[fieldName] = targetKey ? related[targetKey] : '';
      });
      return augmented;
    });
  }, [formattedAllData, derivedDatasheetColumns, derivedFieldCache, fieldMetadata]);

  // Get list panel config - use _Display suffix for FK fields
  const listPanelConfig = useMemo(() => {
    const sampleRow = (LARGE_TABLES.has(tableName) ? listData[0] : allData[0]) || {};

    // Helper to get the right field key (use _Display suffix if available)
    const getFieldKey = (fieldName) => {
      if (!fieldName) return '';
      const baseKey = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
      const displayKey = baseKey + '_Display';
      if (sampleRow[displayKey] !== undefined) return displayKey;
      // Case-insensitive fallback for all-caps fields like EU → eu
      if (sampleRow[baseKey] === undefined) {
        const ciKey = Object.keys(sampleRow).find(k => k.toLowerCase() === fieldName.toLowerCase());
        if (ciKey) return ciKey;
      }
      return baseKey;
    };

    if (!moduleConfig?.listPanel) {
      // Default to first two text fields
      const fields = moduleConfig?.detailPanel?.fields || [];
      const textFields = fields.filter(f => f.fieldType !== 'section' && f.fieldType !== 'checkbox');
      return {
        primaryField: textFields[0] ? getFieldKey(textFields[0].fieldName) : 'id',
        secondaryField: textFields[1] ? getFieldKey(textFields[1].fieldName) : ''
      };
    }
    const lp = moduleConfig.listPanel;
    return {
      primaryField: lp.primaryLine ? getFieldKey(lp.primaryLine) : 'id',
      secondaryField: lp.secondaryLine ? getFieldKey(lp.secondaryLine) : ''
    };
  }, [moduleConfig, allData, listData, tableName]);

  // Organize fields by row for rendering
  const fieldsByRow = useMemo(() => {
    if (!moduleConfig?.detailPanel?.fields) return {};
    const rows = {};
    moduleConfig.detailPanel.fields.forEach(field => {
      const row = field.row || 1;
      if (!rows[row]) rows[row] = [];
      rows[row].push(field);
    });
    Object.keys(rows).forEach(row => {
      rows[row].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return rows;
  }, [moduleConfig]);

  // Filter and sort data (uses formatted data for display)
  // Filtering fires only when activeSearch changes (set on Enter submit), not on every keystroke
  const filteredData = useMemo(() => {
    let result = augmentedData.filter(item => {
      // Search filter — applied only when activeSearch is set
      if (activeSearch) {
        const searchLower = activeSearch.toLowerCase();
        const matches = Object.values(item).some(val =>
          String(val || '').toLowerCase().includes(searchLower)
        );
        if (!matches) return false;
      }
      // Column filters (dropdown checkboxes)
      const columnMatch = Object.entries(filters)
        .every(([k, vals]) => !vals?.length || vals.includes(String(item[k] || '')));
      return columnMatch;
    });

    // Apply sorting from listPanel config
    if (moduleConfig?.listPanel?.sortField) {
      const sortField = moduleConfig.listPanel.sortField;
      const direction = moduleConfig.listPanel.sortDirection === 'DESC' ? -1 : 1;
      const sampleRow = result[0] || {};

      // Determine the correct key (check for _Display suffix for FK fields)
      const baseKey = sortField.charAt(0).toLowerCase() + sortField.slice(1);
      const displayKey = baseKey + '_Display';
      const sortKey = sampleRow[displayKey] !== undefined ? displayKey : baseKey;

      result = [...result].sort((a, b) => {
        const aVal = a[sortKey] ?? '';
        const bVal = b[sortKey] ?? '';
        return String(aVal).localeCompare(String(bVal)) * direction;
      });
    }

    return result;
  }, [augmentedData, activeSearch, filters, moduleConfig]);

  // List panel items — large tables use listData / server search results; non-large use filteredData
  // Column filters always cascade to list view (restores pre-split-load behavior)
  const listPanelItems = useMemo(() => {
    if (!LARGE_TABLES.has(tableName)) return filteredData;
    const hasActiveFilters = Object.values(filters).some(f => f?.length > 0);
    if (hasActiveFilters) return filteredData; // column filters cascade to list (uses full datasheetData)
    if (isListSearchActive) return listSearchResults; // server search results
    return listData; // default: top 100
  }, [tableName, filteredData, filters, isListSearchActive, listSearchResults, listData]);

  const isDirty = Object.keys(editedData).length > 0;

  // Pagination — always active for datasheet view
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Reset to page 1 when filters change or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeSearch, pageSize]);

  // Selection handler that updates URL - warns if unsaved changes
  const handleSelectItem = useCallback((item) => {
    // If editing with unsaved changes, confirm before switching
    if (isEditing && dirtyFields.size > 0 && item?.id !== selectedItem?.id) {
      notify.confirm(
        'You have unsaved changes. Discard them?',
        () => {
          setEditedRecord(null);
          setDirtyFields(new Set());
          setIsEditing(false);
          setSelectedItem(item);
          if (item) {
            updateUrl(item.id, searchTerm, !listPanelCollapsed, relatedPanelCollapsed);
          }
        },
        { confirmText: 'Discard', cancelText: 'Keep Editing' }
      );
      return;
    }
    // Clear edit state when switching items
    if (isEditing) {
      setEditedRecord(null);
      setDirtyFields(new Set());
      setIsEditing(false);
    }
    setSelectedItem(item);
    if (item) {
      updateUrl(item.id, searchTerm, !listPanelCollapsed, relatedPanelCollapsed);
    }
  }, [updateUrl, searchTerm, listPanelCollapsed, relatedPanelCollapsed, isEditing, dirtyFields, selectedItem, notify]);

  // Search input change — updates display only, does not trigger filtering
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  // Search submit (Enter key) — fires the actual filter and updates URL
  // Large tables: also fires server-side search to populate List View results
  const handleSearchSubmit = useCallback(async (value) => {
    setActiveSearch(value); // drives datasheet client-side filter
    updateUrl(selectedItem?.id, value, !listPanelCollapsed, relatedPanelCollapsed);

    if (LARGE_TABLES.has(tableName)) {
      if (!value) {
        setIsListSearchActive(false);
        setListSearchResults([]);
      } else {
        try {
          setSearchLoading(true);
          const res = await dynamicApi.getAll(tableName, { search: value });
          setListSearchResults(res.data);
          setIsListSearchActive(true);
        } catch (err) {
          console.error('Search failed:', err);
        } finally {
          setSearchLoading(false);
        }
      }
    }
  }, [updateUrl, selectedItem, listPanelCollapsed, relatedPanelCollapsed, tableName]);

  // Panel collapse handler that updates URL
  const handlePanelToggle = useCallback(() => {
    const newCollapsed = !listPanelCollapsed;
    setListPanelCollapsed(newCollapsed);
    updateUrl(selectedItem?.id, searchTerm, !newCollapsed, relatedPanelCollapsed);
  }, [updateUrl, selectedItem, searchTerm, listPanelCollapsed, relatedPanelCollapsed]);

  // Detail panel dirty data check
  const isDetailDirty = dirtyFields.size > 0;

  // Warn before browser navigation/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isEditing && isDetailDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditing, isDetailDirty]);

  // Reset edit state when navigating between tables
  useEffect(() => {
    setIsEditing(false);
    setIsNewRecord(false);
    setEditedRecord(null);
    setDirtyFields(new Set());
    setFkOptions({});
    setFrozenColumns(null); // re-initialize to first column of new table
  }, [tableName]);

  // Default frozen columns to first column when columns first load
  // Wait for moduleConfig so derived-only columns don't get frozen prematurely
  useEffect(() => {
    if (columns.length > 0 && frozenColumns === null && moduleConfig !== null) {
      setFrozenColumns([columns[0].key]);
    }
  }, [columns, frozenColumns, moduleConfig]);

  // CRUD handlers
  const handleAdd = async () => {
    // Build a blank record from the schema fields
    const blankRecord = {};
    (moduleConfig?.detailPanel?.fields || []).forEach(field => {
      if (field.fieldType === 'section' || field.fieldType === 'spacer') return;
      blankRecord[field.fieldName] = null;
    });

    setSelectedItem(null);
    setEditedRecord(blankRecord);
    setDirtyFields(new Set());
    setIsNewRecord(true);
    setIsEditing(true);

    // Pre-fetch FK options same as handleEdit
    const fkTablesToFetch = new Set();
    const fieldFkMap = {};
    (moduleConfig?.detailPanel?.fields || []).forEach(field => {
      const meta = fieldMetadata[field.fieldName];
      const fkTable = meta?.fK_Table;
      if (fkTable && !fkOptions[fkTable]) {
        fkTablesToFetch.add(fkTable);
        fieldFkMap[fkTable] = field.fieldName;
      }
    });
    if (fkTablesToFetch.size > 0) {
      const newOptions = { ...fkOptions };
      await Promise.all([...fkTablesToFetch].map(async (fkTable) => {
        try {
          const res = await dynamicApi.getAll(fkTable);
          const fieldName = fieldFkMap[fkTable];
          const displayField = fieldMetadata[fieldName]?.fK_Display_Field;
          const displayKey = displayField ? displayField.charAt(0).toLowerCase() + displayField.slice(1) : null;
          newOptions[fkTable] = (res.data || []).map(row => ({
            id: row.id,
            display: displayKey ? row[displayKey] : row.id
          })).sort((a, b) => String(a.display).localeCompare(String(b.display)));
        } catch {
          newOptions[fkTable] = [];
        }
      }));
      setFkOptions(newOptions);
    }
  };

  const handleEdit = async () => {
    // Create a copy of the selected item for editing
    if (selectedItem) {
      setEditedRecord({ ...selectedItem });
      setDirtyFields(new Set());
      setIsEditing(true);

      // Fetch FK table options for any FK dropdown fields (from field metadata)
      const fkTablesToFetch = new Set();
      const fieldFkMap = {}; // Map fkTable -> fieldName for display field lookup

      (moduleConfig?.detailPanel?.fields || []).forEach(field => {
        const meta = fieldMetadata[field.fieldName];
        const fkTable = meta?.fK_Table;
        if (fkTable && !fkOptions[fkTable]) {
          fkTablesToFetch.add(fkTable);
          fieldFkMap[fkTable] = field.fieldName;
        }
      });

      if (fkTablesToFetch.size > 0) {
        const newOptions = { ...fkOptions };
        await Promise.all([...fkTablesToFetch].map(async (fkTable) => {
          try {
            const res = await dynamicApi.getAll(fkTable);
            // Build options array with id and display value
            const fieldName = fieldFkMap[fkTable];
            const displayField = fieldMetadata[fieldName]?.fK_Display_Field;
            const displayKey = displayField ? displayField.charAt(0).toLowerCase() + displayField.slice(1) : null;
            newOptions[fkTable] = (res.data || []).map(row => ({
              id: row.id,
              display: displayKey ? row[displayKey] : row.id
            })).sort((a, b) => String(a.display).localeCompare(String(b.display)));
          } catch (err) {
            console.error(`Failed to fetch FK options for ${fkTable}:`, err);
            newOptions[fkTable] = [];
          }
        }));
        setFkOptions(newOptions);
      }
    }
  };

  const handleSave = async () => {
    console.log('handleSave called:', { editedRecord, isDetailDirty, dirtyFields: [...dirtyFields], isNewRecord });
    if (!editedRecord || (!isDetailDirty && !isNewRecord)) {
      console.log('handleSave early return - no editedRecord or not dirty');
      return;
    }
    try {
      // Prepare payload - convert dates to ISO format for API
      const payload = { ...editedRecord };
      Object.keys(payload).forEach(key => {
        const meta = Object.values(fieldMetadata).find(m =>
          m.field_Name?.toLowerCase() === key.toLowerCase()
        );
        // Convert date fields to ISO format if they're in MM/DD/YYYY or YYYY-MM-DD format
        if (meta?.uI_Type === 'date' && payload[key]) {
          const val = payload[key];
          // If it's already ISO format with time, leave it
          if (typeof val === 'string' && !val.includes('T')) {
            // Parse and convert to ISO
            const date = new Date(val);
            if (!isNaN(date.getTime())) {
              payload[key] = date.toISOString();
            }
          }
        }
      });

      // Auto-stamp Rev_Date and Rev_User if they exist on this table
      if (fieldMetadata['Rev_Date']) payload[findRecordKey(payload, 'Rev_Date') || 'rev_Date'] = new Date().toISOString();
      if (fieldMetadata['Rev_User']) payload[findRecordKey(payload, 'Rev_User') || 'rev_User'] = currentUser?.user_Name || '';

      if (isNewRecord) {
        // POST via Dynamic API — create new record
        const createRes = await dynamicApi.create(tableName, payload);
        const newId = createRes.data?.id ?? createRes.data;
        // Fetch the full resolved record
        const recordRes = await dynamicApi.getById(tableName, newId);
        const newRecord = recordRes.data;
        if (LARGE_TABLES.has(tableName)) {
          setDatasheetData(prev => [newRecord, ...prev]);
          setOriginalData(prev => [newRecord, ...prev]);
          setListData(prev => [newRecord, ...prev]);
        } else {
          setAllData(prev => [newRecord, ...prev]);
          setOriginalData(prev => [newRecord, ...prev]);
        }
        setSelectedItem(newRecord);
        setIsNewRecord(false);
        notify.status('Record created');
      } else {
        // PUT via Dynamic API
        console.log('handleSave sending:', { id: payload.id, payload });
        await dynamicApi.update(tableName, payload.id, payload);
        // Fetch the single updated record (fast — gets FK display values resolved)
        const recordRes = await dynamicApi.getById(tableName, payload.id);
        const updatedRecord = recordRes.data;
        // Patch local state — no full table reload needed
        if (LARGE_TABLES.has(tableName)) {
          setDatasheetData(prev => prev.map(r => r.id === updatedRecord.id ? updatedRecord : r));
          setOriginalData(prev => prev.map(r => r.id === updatedRecord.id ? updatedRecord : r));
          setListData(prev => prev.map(r => r.id === updatedRecord.id ? updatedRecord : r));
        } else {
          setAllData(prev => prev.map(r => r.id === updatedRecord.id ? updatedRecord : r));
          setOriginalData(prev => prev.map(r => r.id === updatedRecord.id ? updatedRecord : r));
        }
        setSelectedItem(updatedRecord);
        notify.status('Record saved');
      }
      // Clear edit state
      setEditedRecord(null);
      setDirtyFields(new Set());
      setIsEditing(false);
    } catch (err) {
      console.error('Save failed:', err);
      const errDetail = err.response?.data?.error || err.response?.data?.message || err.response?.data || err.message;
      notify.error('Save failed: ' + (typeof errDetail === 'string' ? errDetail : JSON.stringify(errDetail)));
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedItem) return;
    notify.confirm(
      'Delete this record? This cannot be undone.',
      async () => {
        try {
          await dynamicApi.delete(tableName, selectedItem.id);
          const deletedId = selectedItem.id;
          if (LARGE_TABLES.has(tableName)) {
            setDatasheetData(prev => prev.filter(r => r.id !== deletedId));
            setOriginalData(prev => prev.filter(r => r.id !== deletedId));
            setListData(prev => prev.filter(r => r.id !== deletedId));
          } else {
            setAllData(prev => prev.filter(r => r.id !== deletedId));
            setOriginalData(prev => prev.filter(r => r.id !== deletedId));
          }
          setSelectedItem(null);
          setEditedRecord(null);
          setDirtyFields(new Set());
          setIsEditing(false);
          notify.status('Record deleted');
        } catch (err) {
          console.error('Delete failed:', err);
          const errDetail = err.response?.data?.error || err.response?.data?.message || err.message;
          notify.error('Delete failed: ' + (typeof errDetail === 'string' ? errDetail : JSON.stringify(errDetail)));
        }
      },
      { confirmText: 'Delete', cancelText: 'Cancel' }
    );
  };

  const handleCancel = () => {
    if (dirtyFields.size > 0) {
      // Show confirmation if there are unsaved changes
      notify.confirm(
        'You have unsaved changes. Discard them?',
        () => {
          // User confirmed - discard changes
          setEditedRecord(null);
          setDirtyFields(new Set());
          setIsEditing(false);
          setIsNewRecord(false);
        },
        { confirmText: 'Discard', cancelText: 'Keep Editing' }
      );
    } else {
      // No changes - just exit edit mode
      setEditedRecord(null);
      setDirtyFields(new Set());
      setIsEditing(false);
      setIsNewRecord(false);
    }
  };

  // Handle field value change during edit
  const handleFieldChange = (fieldName, value) => {
    console.log('handleFieldChange called:', { fieldName, value, hasEditedRecord: !!editedRecord });
    if (!editedRecord) return;
    // Use case-insensitive key lookup to match API response keys
    const key = findRecordKey(editedRecord, fieldName);
    console.log('handleFieldChange updating:', { key, value });
    setEditedRecord(prev => {
      const updates = { ...prev, [key]: value };
      // Auto-stamp CS/QA/OAT Date+User when the corresponding Complete field changes
      const completeMatch = fieldName.match(/^(CS|QA|OAT)_Complete$/i);
      if (completeMatch && fieldMetadata) {
        const prefix = completeMatch[1].toUpperCase();
        const stampDate = new Date().toISOString();
        const stampUser = currentUser?.user_Name || '';
        const dateKey = findRecordKey(prev, `${prefix}_Date`);
        const userKey = findRecordKey(prev, `${prefix}_User`);
        if (dateKey) updates[dateKey] = stampDate;
        if (userKey) updates[userKey] = stampUser;
        // If OAT_Complete set, cascade to CS/QA if they're currently empty
        if (prefix === 'OAT' && value) {
          ['CS', 'QA'].forEach(p => {
            const completeKey = findRecordKey(prev, `${p}_Complete`);
            if (completeKey && !prev[completeKey]) {
              updates[completeKey] = value;
              const dk = findRecordKey(prev, `${p}_Date`);
              const uk = findRecordKey(prev, `${p}_User`);
              if (dk) updates[dk] = stampDate;
              if (uk) updates[uk] = stampUser;
            }
          });
        }
      }
      return updates;
    });
    // Track dirty field - also use case-insensitive lookup for original
    const originalKey = findRecordKey(selectedItem, fieldName);
    const originalValue = selectedItem?.[originalKey];
    if (value !== originalValue) {
      setDirtyFields(prev => new Set([...prev, fieldName]));
    } else {
      setDirtyFields(prev => {
        const next = new Set(prev);
        next.delete(fieldName);
        return next;
      });
    }
  };

  // Datasheet cell change handler
  const handleCellChange = useCallback((rowId, fieldName, value) => {
    setEditedData(prev => {
      const rowEdits = prev[rowId] || {};
      const originalRow = originalData.find(r => r.id === rowId);
      const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
      const originalValue = originalRow?.[key];
      const meta = fieldMetadata[fieldName];
      const uiType = meta?.uI_Type?.toLowerCase() || 'text';

      // Normalize comparison based on field type
      const valuesMatch = () => {
        // Both null/empty
        if ((value === null || value === undefined || value === '') &&
            (originalValue === null || originalValue === undefined || originalValue === '')) {
          return true;
        }
        // Date comparison - compare just YYYY-MM-DD portion
        if (uiType === 'date') {
          const newDate = value ? value.split('T')[0] : '';
          const origDate = originalValue ? String(originalValue).split('T')[0] : '';
          return newDate === origDate;
        }
        // Number comparison
        if (uiType === 'number') {
          return Number(value) === Number(originalValue);
        }
        // Boolean comparison
        if (uiType === 'checkbox' || typeof originalValue === 'boolean') {
          return Boolean(value) === Boolean(originalValue);
        }
        // Default string comparison
        return value === originalValue;
      };

      // If matches original, remove from edits
      if (valuesMatch()) {
        const { [fieldName]: _, ...remaining } = rowEdits;
        if (Object.keys(remaining).length === 0) {
          const { [rowId]: __, ...remainingRows } = prev;
          return remainingRows;
        }
        return { ...prev, [rowId]: remaining };
      }

      const newRowEdits = { ...rowEdits, [fieldName]: value };
      // Auto-stamp CS/QA/OAT Date+User when the corresponding Complete field changes
      const completeMatch = fieldName.match(/^(CS|QA|OAT)_Complete$/i);
      if (completeMatch && fieldMetadata) {
        const prefix = completeMatch[1].toUpperCase();
        const stampDate = new Date().toISOString();
        const stampUser = currentUser?.user_Name || '';
        if (fieldMetadata[`${prefix}_Date`]) newRowEdits[`${prefix}_Date`] = stampDate;
        if (fieldMetadata[`${prefix}_User`]) newRowEdits[`${prefix}_User`] = stampUser;
        // If OAT_Complete set, cascade to CS/QA if they're currently empty
        if (prefix === 'OAT' && value) {
          ['CS', 'QA'].forEach(p => {
            const currentVal = newRowEdits[`${p}_Complete`] ?? originalRow?.[`${p.toLowerCase()}_Complete`];
            if (!currentVal && fieldMetadata[`${p}_Complete`]) {
              newRowEdits[`${p}_Complete`] = value;
              if (fieldMetadata[`${p}_Date`]) newRowEdits[`${p}_Date`] = stampDate;
              if (fieldMetadata[`${p}_User`]) newRowEdits[`${p}_User`] = stampUser;
            }
          });
        }
      }
      return { ...prev, [rowId]: newRowEdits };
    });
  }, [originalData, fieldMetadata, currentUser]);

  // Render editable cell based on field type
  const renderDatasheetCell = useCallback((row, column, value, isEdited) => {
    const fieldName = column.fieldName;
    const meta = fieldMetadata[fieldName];
    const uiType = meta?.uI_Type?.toLowerCase() || 'text';
    const rowId = row.id;

    // Lock all cells while save is in progress
    if (isDatasheetSaving) {
      return <span className="readonly-cell">{formatDateValue(value, fieldName) ?? value ?? ''}</span>;
    }

    // Per-module display-only override (not a global readonly — just non-editable in this context)
    const entryMode = fieldOverrides?.[fieldName]?.entryMode;
    if (entryMode === 'display-only') {
      return <span className="field-display-only">{formatDateValue(value, fieldName) ?? value ?? ''}</span>;
    }

    const handleChange = (newValue) => {
      handleCellChange(rowId, fieldName, newValue);
    };

    // Checkbox
    if (uiType === 'checkbox' || typeof value === 'boolean') {
      return (
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => handleChange(e.target.checked)}
        />
      );
    }

    // FK field - searchselect or regular dropdown
    if (meta?.fK_Table) {
      const options = fkOptions[meta.fK_Table] || [];
      const selectedOpt = options.find(o => o.id === value);

      if (uiType === 'searchselect') {
        return (
          <SearchSelect
            value={value}
            displayValue={selectedOpt?.display || ''}
            options={options}
            onChange={handleChange}
            isLookup={false}
            className={isEdited ? 'dirty' : ''}
          />
        );
      }

      return (
        <select value={value || ''} onChange={e => handleChange(e.target.value ? Number(e.target.value) : null)}>
          <option value="">--</option>
          {options.map(opt => <option key={opt.id} value={opt.id}>{opt.display}</option>)}
        </select>
      );
    }

    // Lookup category - searchselect or regular dropdown
    if (meta?.lookup_Category) {
      const options = getLookupValues(meta.lookup_Category);

      if (uiType === 'searchselect') {
        return (
          <SearchSelect
            value={value}
            displayValue={value || ''}
            options={options}
            onChange={handleChange}
            isLookup={true}
            className={isEdited ? 'dirty' : ''}
          />
        );
      }

      return (
        <select value={value || ''} onChange={e => handleChange(e.target.value)}>
          <option value="">--</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }

    // Date
    if (uiType === 'date') {
      // Convert display format back to ISO for input
      let dateVal = '';
      if (value) {
        try {
          // Handle MM/DD/YYYY format
          if (typeof value === 'string' && value.includes('/')) {
            const [month, day, year] = value.split('/');
            dateVal = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          } else {
            dateVal = new Date(value).toISOString().split('T')[0];
          }
        } catch {
          dateVal = '';
        }
      }
      return (
        <input
          type="date"
          value={dateVal}
          onChange={e => handleChange(e.target.value)}
        />
      );
    }

    // Number
    if (uiType === 'number') {
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={e => handleChange(e.target.value ? Number(e.target.value) : null)}
        />
      );
    }

    // Currency — number input in edit, formatted display otherwise
    if (uiType === 'currency') {
      return (
        <input
          type="number"
          step="0.01"
          value={value ?? ''}
          onChange={e => handleChange(e.target.value ? Number(e.target.value) : null)}
        />
      );
    }

    // Readonly (computed columns)
    if (uiType === 'readonly') {
      return <span className="readonly-cell">{formatDateValue(value, fieldName) ?? value ?? ''}</span>;
    }

    // URL - input in edit, clickable in display
    if (uiType === 'url') {
      return (
        <input
          type="url"
          value={value ?? ''}
          onChange={e => handleChange(e.target.value)}
          placeholder="https://..."
        />
      );
    }

    // Default: text
    return (
      <input
        type="text"
        value={value ?? ''}
        onChange={e => handleChange(e.target.value)}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldMetadata, fkOptions, getLookupValues, handleCellChange, isDatasheetSaving]);

  // Datasheet edit mode toggle - fetch FK options when entering edit mode
  const handleEditModeToggle = async () => {
    if (!editModeEnabled) {
      // Entering edit mode - fetch FK options for FK columns
      const fkColumns = columns.filter(col => fieldMetadata[col.fieldName]?.fK_Table);
      if (fkColumns.length > 0) {
        const newOptions = { ...fkOptions };
        await Promise.all(fkColumns.map(async (col) => {
          const fkTable = fieldMetadata[col.fieldName].fK_Table;
          if (!newOptions[fkTable]) {
            try {
              const res = await dynamicApi.getAll(fkTable);
              const displayField = fieldMetadata[col.fieldName].fK_Display_Field;
              const displayKey = displayField?.charAt(0).toLowerCase() + displayField?.slice(1);
              newOptions[fkTable] = (res.data || []).map(row => ({
                id: row.id,
                display: displayKey ? row[displayKey] : row.id
              })).sort((a, b) => String(a.display).localeCompare(String(b.display)));
            } catch (err) {
              console.error(`Failed to fetch FK options for ${fkTable}:`, err);
              newOptions[fkTable] = [];
            }
          }
        }));
        setFkOptions(newOptions);
      }
      setEditModeEnabled(true);
      setCurrentPage(1); // Reset pagination when entering edit mode
    } else {
      // Exiting edit mode
      handleDatasheetCancel();
    }
  };

  // Save all edited rows
  const handleDatasheetSave = async () => {
    if (!isDirty) return;
    setIsDatasheetSaving(true);

    try {
      const updates = Object.entries(editedData).map(([rowId, changes]) => {
        const original = originalData.find(r => r.id === parseInt(rowId));
        const payload = { ...original };
        // Apply changes with camelCase keys
        Object.entries(changes).forEach(([fieldName, value]) => {
          const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
          payload[key] = value;
        });
        return { id: parseInt(rowId), payload };
      });

      // Auto-stamp Rev_Date and Rev_User on every row if they exist on this table
      const stampDate = new Date().toISOString();
      const stampUser = currentUser?.user_Name || '';
      updates.forEach(({ payload: p }) => {
        if (fieldMetadata['Rev_Date']) p[findRecordKey(p, 'Rev_Date') || 'rev_Date'] = stampDate;
        if (fieldMetadata['Rev_User']) p[findRecordKey(p, 'Rev_User') || 'rev_User'] = stampUser;
      });

      // Save each row
      await Promise.all(
        updates.map(({ id, payload }) => dynamicApi.update(tableName, id, payload))
      );

      // Refresh data
      const dataRes = await dynamicApi.getAll(tableName);
      if (LARGE_TABLES.has(tableName)) {
        setDatasheetData(dataRes.data);
        setOriginalData(dataRes.data);
        // Patch updated records in listData
        const updatedIds = new Set(updates.map(u => u.id));
        setListData(prev => prev.map(r => {
          if (!updatedIds.has(r.id)) return r;
          return dataRes.data.find(d => d.id === r.id) || r;
        }));
      } else {
        setAllData(dataRes.data);
        setOriginalData(dataRes.data);
      }
      setEditedData({});
      setEditModeEnabled(false);

      notify.status(`Saved ${updates.length} record(s)`);
    } catch (err) {
      notify.error('Save failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsDatasheetSaving(false);
    }
  };

  // Cancel with confirmation if dirty
  const handleDatasheetCancel = () => {
    if (isDirty) {
      notify.confirm('Discard unsaved changes?', () => {
        setEditedData({});
        if (LARGE_TABLES.has(tableName)) {
          setDatasheetData([...originalData]);
        } else {
          setAllData([...originalData]);
        }
        setEditModeEnabled(false);
      }, { confirmText: 'Discard', cancelText: 'Keep Editing' });
    } else {
      setEditModeEnabled(false);
    }
  };

  // Delete mode handlers
  const handleEnterDeleteMode = () => {
    setDeleteModeEnabled(true);
    setSelectedForDelete(new Set());
    setCurrentPage(1); // Reset to first page
  };

  const handleCancelDeleteMode = () => {
    setDeleteModeEnabled(false);
    setSelectedForDelete(new Set());
  };

  const handleToggleSelectRow = (rowId) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const handleSelectAllOnPage = () => {
    const pageIds = paginatedData.map(row => row.id);
    const allSelected = pageIds.every(id => selectedForDelete.has(id));
    if (allSelected) {
      // Deselect all on page
      setSelectedForDelete(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select all on page
      setSelectedForDelete(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const handleDeleteSelected = async () => {
    const count = selectedForDelete.size;
    if (count === 0) return;

    notify.confirm(
      `You are about to permanently delete ${count} record${count > 1 ? 's' : ''}. This action cannot be undone.`,
      async () => {
        try {
          await dynamicApi.deleteMultiple(tableName, Array.from(selectedForDelete));
          notify.status(`Deleted ${count} record${count > 1 ? 's' : ''}`);
          handleCancelDeleteMode();
          // Refresh data
          const dataRes = await dynamicApi.getAll(tableName);
          if (LARGE_TABLES.has(tableName)) {
            setDatasheetData(dataRes.data);
            setOriginalData(dataRes.data);
            setListData(prev => prev.filter(r => !selectedForDelete.has(r.id)));
          } else {
            setAllData(dataRes.data);
            setOriginalData(dataRes.data);
          }
        } catch (err) {
          const message = err.response?.data?.message || err.message || 'Delete failed';
          notify.error(message);
        }
      },
      { confirmText: 'Delete', cancelText: 'Cancel' }
    );
  };

  // Clear selections when page changes in delete mode
  useEffect(() => {
    if (deleteModeEnabled) {
      setSelectedForDelete(new Set());
    }
  }, [currentPage, deleteModeEnabled]);

  // Related panel resize handlers
  const handleRelatedResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleRelatedResizeMove = useCallback((e) => {
    if (!isResizing || !detailPanelRef.current) return;

    const panelRect = detailPanelRef.current.getBoundingClientRect();
    const mouseY = e.clientY;
    const panelBottom = panelRect.bottom;
    const panelTop = panelRect.top;
    const panelHeight = panelBottom - panelTop;

    // Calculate percentage from bottom (where related panel is)
    const distanceFromBottom = panelBottom - mouseY;
    let percentage = (distanceFromBottom / panelHeight) * 100;

    // Clamp between 10% and 60%
    percentage = Math.max(10, Math.min(60, percentage));
    setRelatedPanelHeight(percentage);
  }, [isResizing]);

  const handleRelatedResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove mouse move and up listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleRelatedResizeMove);
      document.addEventListener('mouseup', handleRelatedResizeEnd);
    }
    return () => {
      document.removeEventListener('mousemove', handleRelatedResizeMove);
      document.removeEventListener('mouseup', handleRelatedResizeEnd);
    };
  }, [isResizing, handleRelatedResizeMove, handleRelatedResizeEnd]);

  // Reset related panel height on navigation (when selectedItem changes)
  useEffect(() => {
    setRelatedPanelHeight(33);
    setRelatedPanelCollapsed(false);
  }, [selectedItem?.id]);

  // Toggle related panel collapsed and update URL
  const handleToggleRelatedCollapsed = useCallback(() => {
    const newCollapsed = !relatedPanelCollapsed;
    setRelatedPanelCollapsed(newCollapsed);
    updateUrl(selectedItem?.id, searchTerm, !listPanelCollapsed, newCollapsed);
  }, [updateUrl, selectedItem, searchTerm, listPanelCollapsed, relatedPanelCollapsed]);

  // FK Modal handler - opens popup instead of navigating
  const openFKModal = (targetTable, recordId) => {
    setFkModalTable(targetTable);
    setFkModalRecordId(recordId);
    setFkModalOpen(true);
  };

  const closeFKModal = () => {
    setFkModalOpen(false);
    setFkModalTable(null);
    setFkModalRecordId(null);
  };

  // Find the actual key in the record (case-insensitive match)
  const findRecordKey = (record, fieldName) => {
    if (!record) return null;
    // Try exact camelCase first
    const camelKey = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    if (record.hasOwnProperty(camelKey)) return camelKey;
    // Try all lowercase (handles "QAQC" -> "qaqc")
    const lowerKey = fieldName.toLowerCase();
    if (record.hasOwnProperty(lowerKey)) return lowerKey;
    // Search case-insensitively
    const keys = Object.keys(record);
    return keys.find(k => k.toLowerCase() === lowerKey) || camelKey;
  };

  // Fetch derived field data when selectedItem changes
  useEffect(() => {
    if (!selectedItem || !moduleConfig?.detailPanel?.fields) return;

    const derivedFields = moduleConfig.detailPanel.fields.filter(f => f.fieldType === 'fk-derived');
    if (derivedFields.length === 0) return;

    // Group by sourceField so we only fetch each related table once
    const sourceFields = [...new Set(derivedFields.map(f => f.sourceField).filter(Boolean))];

    sourceFields.forEach(async (sourceField) => {
      const fkValue = selectedItem[sourceField] ?? selectedItem[Object.keys(selectedItem).find(k => k.toLowerCase() === sourceField.toLowerCase())];
      if (!fkValue) return;

      // Determine the FK table from module config or field metadata
      const configField = moduleConfig.detailPanel.fields.find(f => f.fieldName === sourceField);
      const fkTable = configField?.fkTable || fieldMetadata[sourceField]?.fK_Table;
      if (!fkTable) return;

      const cacheKey = `${fkTable}:${fkValue}`;
      if (derivedFieldCache[cacheKey]) return; // already cached

      try {
        const { dynamicApi } = await import('../../services/api');
        const res = await dynamicApi.getById(fkTable, fkValue);
        setDerivedFieldCache(prev => ({ ...prev, [cacheKey]: res.data }));
      } catch {
        // silently fail — derived fields are display-only
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  // Get field value from selected/edited record
  const getFieldValue = (fieldName) => {
    const record = isEditing && editedRecord ? editedRecord : selectedItem;
    if (!record) return '';
    const key = findRecordKey(record, fieldName);
    return record[key] ?? '';
  };

  // Get FK display value - checks for _Display suffix pattern
  const getFKDisplayValue = (fieldName) => {
    if (!selectedItem) return '';
    const baseKey = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    // Check for _Display suffix first (backend convention)
    const displayKey = baseKey + '_Display';
    if (selectedItem[displayKey] !== undefined) {
      return selectedItem[displayKey];
    }
    // Fallback to base field
    return selectedItem[baseKey] ?? '';
  };

  // Format date value based on UI_Type
  const formatDateValue = (value, fieldName) => {
    if (!value) return '';
    const meta = fieldMetadata[fieldName];
    const uiType = meta?.uI_Type?.toLowerCase();
    const looksLikeDate = uiType === 'date' || uiType === 'readonly';
    if (looksLikeDate && typeof value === 'string' && value.includes('T')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          const year = date.getUTCFullYear();
          return `${month}/${day}/${year}`;
        }
      } catch { /* return original */ }
    }
    if (uiType === 'date') {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        }
      } catch { /* return original */ }
    }
    return value;
  };

  // Render a field based on config
  // UI type comes from Field Manager metadata (uI_Type), with module config fieldType as fallback
  const renderField = (field) => {
    // fk-derived: readonly field pulled from a related record (e.g. Index_Sheet → Manufacturer)
    if (field.fieldType === 'fk-derived') {
      const colSpan = field.colSpan || 8;
      const labelPos = field.labelPosition || 'top';
      const labelAlign = field.labelAlignment || 'left';
      const posClass = labelPos !== 'top' ? ` label-${labelPos}` : '';
      const alignClass = labelAlign !== 'left' ? ` label-align-${labelAlign}` : '';
      const groupClass = `form-group col-span-${colSpan}${posClass}${alignClass}`;

      const sourceField = field.sourceField;
      const targetField = field.targetField;
      const configField = moduleConfig?.detailPanel?.fields?.find(f => f.fieldName === sourceField);
      const fkTable = configField?.fkTable || fieldMetadata[sourceField]?.fK_Table;
      const fkValue = selectedItem?.[sourceField] ?? selectedItem?.[Object.keys(selectedItem || {}).find(k => k.toLowerCase() === sourceField?.toLowerCase())];
      const cacheKey = fkTable && fkValue ? `${fkTable}:${fkValue}` : null;
      const relatedRecord = cacheKey ? derivedFieldCache[cacheKey] : null;

      // Try exact key match first, then case-insensitive
      let displayValue = '';
      if (relatedRecord) {
        const exactKey = Object.keys(relatedRecord).find(k => k === targetField);
        const ciKey = exactKey || Object.keys(relatedRecord).find(k => k.toLowerCase() === targetField?.toLowerCase());
        const rawVal = ciKey ? relatedRecord[ciKey] : '';
        // If it resolved to a display field, use that
        const displayKey = ciKey ? `${ciKey}_Display` : null;
        displayValue = (displayKey && relatedRecord[displayKey]) || rawVal || '';
      }

      const isLoading = fkValue && fkTable && !relatedRecord;

      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input
            type="text"
            value={isLoading ? '…' : String(displayValue)}
            readOnly
            className="form-input derived-field"
          />
        </div>
      );
    }

    if (field.fieldType === 'section') {
      return (
        <div key={field.fieldName} className="col-span-24">
          <div className="detail-section-header">{field.sectionTitle || field.label}</div>
        </div>
      );
    }

    if (field.fieldType === 'spacer') {
      return <div key={field.fieldName} className={`col-span-${field.colSpan || 4}`} />;
    }

    // Per-module entry mode overrides
    const entryMode = fieldOverrides?.[field.fieldName]?.entryMode;
    if (entryMode === 'hidden') return null;

    const value = getFieldValue(field.fieldName);

    if (entryMode === 'display-only') {
      const colSpan = field.colSpan || 8;
      const labelPos = field.labelPosition || 'top';
      const labelAlign = field.labelAlignment || 'left';
      const posClass = labelPos !== 'top' ? ` label-${labelPos}` : '';
      const alignClass = labelAlign !== 'left' ? ` label-align-${labelAlign}` : '';
      const fkMeta = fieldMetadata[field.fieldName];
      const displayOnlyValue = fkMeta?.fK_Table ? getFKDisplayValue(field.fieldName) : (value ?? '');
      return (
        <div key={field.fieldName} className={`form-group col-span-${colSpan}${posClass}${alignClass}`}>
          <label>{field.label}</label>
          <span className="field-display-only">{displayOnlyValue}</span>
        </div>
      );
    }
    const colSpan = field.colSpan || 8;
    const labelPos = field.labelPosition || 'top';
    const labelAlign = field.labelAlignment || 'left';
    const isDirty = dirtyFields.has(field.fieldName);
    // Build class string for label position and alignment
    const posClass = labelPos !== 'top' ? ` label-${labelPos}` : '';
    const alignClass = labelAlign !== 'left' ? ` label-align-${labelAlign}` : '';
    const groupClass = `form-group col-span-${colSpan}${posClass}${alignClass}`;
    const inputClass = `form-input${isDirty ? ' dirty' : ''}`;

    // Determine if field should be readonly (not editing OR user lacks permission)
    const isReadOnly = !isEditing || !userCanEdit;

    // Get UI type from Field Manager metadata (source of truth), fallback to module config
    const meta = fieldMetadata[field.fieldName];
    const uiType = meta?.uI_Type || field.fieldType || 'text';
    const lookupCategory = meta?.lookup_Category || field.lookupCategory;

    // FK table comes from field metadata (single source of truth)
    const fkTable = meta?.fK_Table;

    // FK field - dropdown/searchselect in edit mode, clickable link in view mode
    if (fkTable) {
      const hasValue = value !== null && value !== undefined && value !== '' && value !== 0;
      const options = fkOptions[fkTable] || [];
      // For edit mode, derive display from options; for view mode, use _Display suffix
      const selectedOpt = options.find(o => o.id === value);
      const displayValue = isEditing ? (selectedOpt?.display || '') : getFKDisplayValue(field.fieldName);

      // Edit mode: render as searchselect or dropdown
      if (isEditing && userCanEdit) {
        // Use SearchSelect for searchselect UI type
        if (uiType === 'searchselect') {
          return (
            <div key={field.fieldName} className={groupClass}>
              <label>{field.label}</label>
              <SearchSelect
                value={value}
                displayValue={displayValue}
                options={options}
                onChange={(newVal) => handleFieldChange(field.fieldName, newVal)}
                isLookup={false}
                className={isDirty ? 'dirty' : ''}
              />
            </div>
          );
        }

        // Default: regular dropdown
        return (
          <div key={field.fieldName} className={groupClass}>
            <label>{field.label}</label>
            <select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.value ? Number(e.target.value) : null)}
              className={inputClass}
            >
              <option value="">-- Select --</option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.display}
                </option>
              ))}
            </select>
          </div>
        );
      }

      // View mode: clickable field with FK icon (fkClickable from metadata, defaults to true)
      const fkClickable = meta?.fK_Clickable !== false;
      if (hasValue && fkClickable) {
        return (
          <div key={field.fieldName} className={groupClass}>
            <label>{field.label}</label>
            <div
              className={`form-input fk-field${isDirty ? ' dirty' : ''}`}
              onClick={() => openFKModal(fkTable, value)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && openFKModal(fkTable, value)}
            >
              <span className="fk-field-value">{displayValue || value}</span>
              <Link2 size={12} className="fk-field-icon" />
            </div>
          </div>
        );
      }

      // View mode: no value - render as empty readonly input
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input
            type="text"
            value={displayValue || ''}
            readOnly
            className={inputClass}
          />
        </div>
      );
    }

    if (uiType === 'textarea') {
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <textarea
            value={value}
            readOnly={isReadOnly}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            className={inputClass}
            rows={field.textareaRows || 3}
          />
        </div>
      );
    }

    if (uiType === 'checkbox') {
      return (
        <div key={field.fieldName} className={`form-group col-span-${colSpan}`}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={!!value}
              disabled={isReadOnly}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.checked)}
            />
            <span>{field.label}</span>
          </label>
        </div>
      );
    }

    // Lookup category with searchselect
    if (uiType === 'searchselect' && lookupCategory) {
      const options = getLookupValues(lookupCategory);
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          {isReadOnly ? (
            <input
              type="text"
              value={value}
              readOnly
              className={inputClass}
            />
          ) : (
            <SearchSelect
              value={value}
              displayValue={value || ''}
              options={options}
              onChange={(newVal) => handleFieldChange(field.fieldName, newVal)}
              isLookup={true}
              className={isDirty ? 'dirty' : ''}
            />
          )}
        </div>
      );
    }

    // Lookup category dropdown (non-FK dropdown backed by lookup values)
    if (uiType === 'dropdown' && lookupCategory) {
      const options = getLookupValues(lookupCategory);
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          {isReadOnly ? (
            <input
              type="text"
              value={value}
              readOnly
              className={inputClass}
            />
          ) : (
            <select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
              className={inputClass}
            >
              <option value="">-- Select --</option>
              {options.map((opt, idx) => (
                <option key={idx} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          )}
        </div>
      );
    }

    // Readonly field - always non-editable (e.g., computed columns)
    if (uiType === 'readonly') {
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input
            type="text"
            value={formatDateValue(value, field.fieldName) ?? value ?? ''}
            readOnly
            className="form-input readonly-field"
          />
        </div>
      );
    }

    // URL field - clickable link in view mode, text input in edit mode
    if (uiType === 'url') {
      if (isEditing && !isReadOnly) {
        return (
          <div key={field.fieldName} className={groupClass}>
            <label>{field.label}</label>
            <input
              type="url"
              value={value}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
              className={inputClass}
              placeholder="https://..."
            />
          </div>
        );
      }
      // View mode: clickable link
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          {value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="form-input url-link"
            >
              {value}
            </a>
          ) : (
            <input type="text" value="" readOnly className={inputClass} />
          )}
        </div>
      );
    }

    // Date field - format for display
    if (uiType === 'date') {
      const displayValue = formatDateValue(value, field.fieldName);
      const dateInputValue = value ? (value || '').split('T')[0] : '';

      // When editing: use date input with CSS to hide empty placeholder
      if (isEditing && !isReadOnly) {
        return (
          <div key={field.fieldName} className={groupClass}>
            <label>{field.label}</label>
            <input
              type="date"
              value={dateInputValue}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
              className={`${inputClass}${!dateInputValue ? ' date-empty' : ''}`}
            />
          </div>
        );
      }

      // View mode: show formatted date as text
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input
            type="text"
            value={displayValue}
            readOnly
            className={inputClass}
          />
        </div>
      );
    }

    // Currency — number input in edit, formatted display in read mode
    if (uiType === 'currency') {
      const formatted = value != null && value !== '' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value)) : '';
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          {isReadOnly
            ? <input type="text" value={formatted} readOnly className={inputClass} />
            : <input
                type="number"
                step="0.01"
                value={value ?? ''}
                onChange={(e) => handleFieldChange(field.fieldName, e.target.value ? Number(e.target.value) : null)}
                className={inputClass}
              />
          }
        </div>
      );
    }

    // Default: text input
    return (
      <div key={field.fieldName} className={groupClass}>
        <label>{field.label}</label>
        <input
          type={uiType === 'number' ? 'number' : 'text'}
          value={value}
          readOnly={isReadOnly}
          onChange={(e) => handleFieldChange(field.fieldName, uiType === 'number' ? Number(e.target.value) : e.target.value)}
          className={inputClass}
        />
      </div>
    );
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="module-container">
      <ModuleHeader
        viewMode={forceViewType || viewMode}
        setViewMode={setViewMode}
        forceViewType={forceViewType}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        searchLoading={searchLoading}
        searchPlaceholder={`Search ${tableName.toLowerCase()}...`}
        filters={filters}
        clearAllFilters={() => setFilters({})}
        filteredCount={filteredData.length}
        totalCount={LARGE_TABLES.has(tableName) ? datasheetData.length : allData.length}
        columns={columns}
        filteredData={filteredData}
        filename={tableName.toLowerCase()}
        onAdd={userCanEdit ? handleAdd : null}
        onDelete={userCanEdit ? handleDeleteRecord : null}
        onEdit={userCanEdit ? handleEdit : null}
        onSave={handleSave}
        onCancel={handleCancel}
        canEdit={selectedItem !== null && userCanEdit}
        canSave={isEditing && (isDetailDirty || isNewRecord)}
        isEditing={isEditing}
        isNewRecord={isNewRecord}
        editModeEnabled={editModeEnabled}
        onEditModeToggle={userCanEdit ? handleEditModeToggle : null}
        isDirty={isDirty}
        onDatasheetSave={handleDatasheetSave}
        onDatasheetCancel={handleDatasheetCancel}
        datasheetSaving={isDatasheetSaving}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        frozenColumns={frozenColumns || []}
        onColumnSettingsChange={({ columnOrder: newOrder, hiddenColumns: newHidden, frozenColumns: newFrozen }) => {
          setColumnOrder(newOrder);
          setHiddenColumns(newHidden);
          if (newFrozen !== undefined) setFrozenColumns(newFrozen);
        }}
        userCanEdit={userCanEdit}
        deleteModeEnabled={deleteModeEnabled}
        selectedDeleteCount={selectedForDelete.size}
        onEnterDeleteMode={allowBulkDelete ? handleEnterDeleteMode : null}
        onCancelDeleteMode={handleCancelDeleteMode}
        onConfirmDelete={handleDeleteSelected}
        headerTabs={headerTabs}
      />

      {(forceViewType || viewMode) === 'list' ? (
        <div className={`list-view ${listPanelCollapsed ? 'list-collapsed' : ''}`}>
          <ListPanel
            items={listPanelItems}
            selectedItem={selectedItem}
            onSelectItem={handleSelectItem}
            primaryField={listPanelConfig.primaryField}
            secondaryField={listPanelConfig.secondaryField}
            collapsed={listPanelCollapsed}
            onToggleCollapse={handlePanelToggle}
            scrollToSelected={scrollToSelected}
          />
          <div className={`detail-panel ${isResizing ? 'is-resizing' : ''}`} ref={detailPanelRef}>
            {(selectedItem || isNewRecord) && (
              <>
                {/* Detail Content - scrollable, takes remaining space */}
                <div
                  className="detail-content"
                  style={{
                    height: moduleConfig?.relatedTables?.length > 0 && !relatedPanelCollapsed
                      ? `calc(100% - ${relatedPanelHeight}%)`
                      : '100%'
                  }}
                >
                  <div className="detail-grid-24">
                    {Object.keys(fieldsByRow)
                      .sort((a, b) => Number(a) - Number(b))
                      .map(rowNum => (
                        <React.Fragment key={rowNum}>
                          {fieldsByRow[rowNum].map(field => renderField(field))}
                        </React.Fragment>
                      ))}
                  </div>
                </div>

                {/* Related Tables Section */}
                {moduleConfig?.relatedTables?.length > 0 && (
                  <>
                    <div
                      className={`related-tables-section ${relatedPanelCollapsed ? 'collapsed' : ''}`}
                      style={{ height: relatedPanelCollapsed ? 'auto' : `${relatedPanelHeight}%` }}
                    >
                      <div
                        className={`detail-section-header related-section-header ${!relatedPanelCollapsed ? 'resizable' : ''}`}
                        onMouseDown={!relatedPanelCollapsed ? handleRelatedResizeStart : undefined}
                      >
                        {!relatedPanelCollapsed && (
                          <div className="resize-grip">
                            <GripHorizontal size={14} />
                          </div>
                        )}
                        <span>Related Information</span>
                        <button
                          className="related-collapse-btn"
                          onClick={(e) => { e.stopPropagation(); handleToggleRelatedCollapsed(); }}
                          title={relatedPanelCollapsed ? 'Expand' : 'Collapse'}
                        >
                          {relatedPanelCollapsed ? <ArrowUpWideNarrow size={16} /> : <ArrowDownWideNarrow size={16} />}
                        </button>
                      </div>
                      {!relatedPanelCollapsed && (
                        <>
                    <div className="related-tables-header">
                      <div className="related-tables-tabs">
                        {moduleConfig.relatedTables.map((tab, idx) => {
                          const isTabLoading = relatedLoading[tab.tableName];
                          const tabData = relatedData[tab.tableName];
                          const count = tabData?.length || 0;
                          return (
                            <button
                              key={idx}
                              className={`related-tab ${activeRelatedTab === idx ? 'active' : ''} ${isTabLoading ? 'loading' : ''}`}
                              onClick={() => {
                                setActiveRelatedTab(idx);
                                // Clear filters when changing tabs
                                setRelatedFilters(prev => ({ ...prev, [tab.tableName]: {} }));
                              }}
                            >
                              {tab.label}
                              {isTabLoading ? (
                                <Loader size={12} className="tab-spinner" />
                              ) : count > 0 ? (
                                <span className="related-tab-count">({count})</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                      {(() => {
                        const activeTab = moduleConfig.relatedTables[activeRelatedTab];
                        const tableFilters = relatedFilters[activeTab?.tableName] || {};
                        const hasActiveFilters = Object.values(tableFilters).some(f => f?.length > 0);
                        const totalCount = (relatedData[activeTab?.tableName] || []).length;
                        // Calculate filtered count
                        let filteredCount = totalCount;
                        if (hasActiveFilters) {
                          const tabData = relatedData[activeTab?.tableName] || [];
                          filteredCount = tabData.filter(row => {
                            return Object.entries(tableFilters).every(([colKey, selectedValues]) => {
                              if (!selectedValues || selectedValues.length === 0) return true;
                              const cellValue = String(row[colKey] || '');
                              return selectedValues.includes(cellValue);
                            });
                          }).length;
                        }
                        return hasActiveFilters ? (
                          <button
                            className="clear-filter-btn"
                            onClick={() => setRelatedFilters(prev => ({ ...prev, [activeTab.tableName]: {} }))}
                            title="Clear all filters"
                          >
                            <FilterX size={16} />
                            <span>{filteredCount}/{totalCount}</span>
                          </button>
                        ) : null;
                      })()}
                    </div>
                    <div className="related-tables-content">
                      {moduleConfig.relatedTables[activeRelatedTab] && (() => {
                        const activeTab = moduleConfig.relatedTables[activeRelatedTab];

                        // Show loading state
                        if (relatedLoading[activeTab.tableName]) {
                          return (
                            <div className="related-grid-loading">
                              <Loader size={24} className="spinning" />
                              <span>Loading {activeTab.label}...</span>
                            </div>
                          );
                        }

                        const tabData = relatedData[activeTab.tableName] || [];

                        // Get metadata for this related table
                        const tableMeta = relatedMetadata[activeTab.tableName] || {};

                        // Helper to format date values
                        const formatDate = (value, uiType) => {
                          if (!value) return '';
                          try {
                            const date = new Date(value);
                            if (isNaN(date.getTime())) return value;
                            if (uiType === 'date' || uiType === 'readonly') {
                              const fmt = appSettings['Date_Format'] || 'MM/DD/YYYY';
                              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                              const day = String(date.getUTCDate()).padStart(2, '0');
                              const year = date.getUTCFullYear();
                              return fmt.replace('MM', month).replace('DD', day).replace('YYYY', year);
                            }
                            return value;
                          } catch {
                            return value;
                          }
                        };

                        // Convert columns to FilterableDataGrid format
                        // Check first row for available keys to determine FK display pattern
                        const sampleRow = (tabData || [])[0] || {};

                        const gridColumns = (activeTab.columns || []).map(col => {
                          const meta = tableMeta[col.fieldName];
                          const baseKey = col.fieldName.charAt(0).toLowerCase() + col.fieldName.slice(1);

                          // Check for _Display suffix pattern (backend convention)
                          // e.g., project_Number_Display for FK field Project_Number
                          let key = baseKey;
                          const displaySuffixKey = baseKey + '_Display';
                          if (sampleRow[displaySuffixKey] !== undefined) {
                            key = displaySuffixKey;
                          } else if (meta?.fK_Display_Field) {
                            // Fallback to metadata FK display field
                            const metaKey = meta.fK_Display_Field.charAt(0).toLowerCase() + meta.fK_Display_Field.slice(1);
                            if (sampleRow[metaKey] !== undefined) {
                              key = metaKey;
                            }
                          }

                          return {
                            key: key,
                            label: meta?.display_Label || col.label,
                            width: meta?.default_Width || 150,
                            uiType: meta?.uI_Type, // Pass through for formatting
                            headerVertical: meta?.header_Vertical || false
                          };
                        });

                        // Transform data to format dates
                        const formattedData = (tabData || []).map(row => {
                          const formatted = { ...row };
                          gridColumns.forEach(col => {
                            if (col.uiType === 'date' && formatted[col.key]) {
                              formatted[col.key] = formatDate(formatted[col.key], 'date');
                            }
                          });
                          return formatted;
                        });

                        // Get or initialize filters for this table
                        const tableFilters = relatedFilters[activeTab.tableName] || {};
                        const setTableFilters = (newFilters) => {
                          if (typeof newFilters === 'function') {
                            setRelatedFilters(prev => ({
                              ...prev,
                              [activeTab.tableName]: newFilters(prev[activeTab.tableName] || {})
                            }));
                          } else {
                            setRelatedFilters(prev => ({
                              ...prev,
                              [activeTab.tableName]: newFilters
                            }));
                          }
                        };

                        return (
                          <div className="related-table-grid-container">
                            <FilterableDataGrid
                              key={`${activeTab.tableName}-${formattedData.length}`}
                              data={formattedData}
                              allData={formattedData}
                              columns={gridColumns}
                              filters={tableFilters}
                              setFilters={setTableFilters}
                              onRowDoubleClick={(row) => openFKModal(activeTab.tableName, row.id)}
                            />
                          </div>
                        );
                      })()}
                    </div>
                        </>
                      )}
                  </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="datasheet-view">
          {LARGE_TABLES.has(tableName) && datasheetLoading && (
            <div className="loading-overlay">
              <Loader size={24} className="spinning" />
              <span>Loading full dataset...</span>
            </div>
          )}
          <FilterableDataGrid
            data={paginatedData}
            allData={augmentedData}
            columns={columns}
            filters={filters}
            setFilters={setFilters}
            columnOrder={columnOrder}
            hiddenColumns={hiddenColumns}
            columnWidths={columnWidths}
            frozenColumns={frozenColumns || []}
            onColumnResize={(key, width) => setColumnWidths(prev => ({ ...prev, [key]: width }))}
            editModeEnabled={editModeEnabled}
            editedData={editedData}
            renderEditableCell={renderDatasheetCell}
            deleteModeEnabled={deleteModeEnabled}
            selectedForDelete={selectedForDelete}
            onToggleSelectRow={handleToggleSelectRow}
            onSelectAllOnPage={handleSelectAllOnPage}
          />
          {/* Pagination controls — always shown in datasheet view */}
          {(
            <div className="datasheet-pagination">
              <div className="pagination-info">
                <span>Showing {paginatedData.length} of {filteredData.length} records</span>
              </div>
              <div className="pagination-controls">
                <label className="pagination-pagesize">
                  <span>Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <div className="pagination-nav">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="First page"
                  >
                    <ChevronFirst size={18} />
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="pagination-page-info">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    title="Next page"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    title="Last page"
                  >
                    <ChevronLast size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FK Record Modal */}
      <FKRecordModal
        isOpen={fkModalOpen}
        onClose={closeFKModal}
        initialTableName={fkModalTable}
        initialRecordId={fkModalRecordId}
      />
    </div>
  );
};

export default DynamicModule;
